import * as Yup from "yup";
import moment from "moment";
/* =====================================================
   SUBSCRIPTION PLAN SCHEMA
===================================================== */
export const SubscriptionPlanSchema = Yup.object().shape({
  addInSubscriptionId: Yup.number().nullable(),
  frequency: Yup.number()
    .positive("Frequency is required")
    .typeError("Frequency is required")
    .required("Frequency is required"),
  baseValue: Yup.string()
    .required("Base Cost is required")
    .test("is-valid-cost", "Base Cost must be a valid amount", (value) => {
      if (!value) return false;
      const num = Number.parseFloat(value);
      return !Number.isNaN(num) && num >= 0;
    }),
  effectiveFromDate: Yup.date()
    .nullable()
    .required("Effective From Date is required")
    .when("addInSubscriptionId", (addInSubscriptionId, schema) => {
      // Skip min date validation if editing existing subscription plan (addInSubscriptionId > 0)
      if (addInSubscriptionId && addInSubscriptionId > 0) {
        return schema;
      }
      // For new subscription plans, require current or future date
      return schema.min(
        moment().startOf("day"),
        "Date must be current or future",
      );
    })
    .typeError("Invalid date"),
  effectiveToDate: Yup.date()
    .nullable()
    .when("effectiveFromDate", (effectiveFromDate, schema) => {
      if (effectiveFromDate) {
        return schema.min(
          Yup.ref("effectiveFromDate"),
          "Must be ≥ Effective From Date",
        );
      }
      return schema;
    })
    .typeError("Invalid date"),
  trialDurationDays: Yup.number()
    .nullable()
    .when("enableTrial", (enableTrial, schema) => {
      if (enableTrial) {
        return schema
          .required("Trial Duration is required when trial is enabled")
          .positive("Trial Duration is required when trial is enabled");
      }
      return schema.nullable(true);
    }),
});

export const EnterpriseSubscriptionPlanSchema = SubscriptionPlanSchema.shape({
  enterpriseGroupIds: Yup.array()
    .min(1, "At least one Enterprise Group must be selected")
    .required("At least one Enterprise Group must be selected"),
});

/* =====================================================
   ADD-INS SCHEMAS (Step-wise validation)
===================================================== */

// Step 1: Basic Information validation
const AddInsStep1Schema = {
  addInType: Yup.number()
    .oneOf([1, 2], "Invalid Add-in Type")
    .required("Add-in Type is required"),
  vendorId: Yup.number()
    .positive("Vendor is required")
    .typeError("Vendor is required")
    .required("Vendor is required"),
  integrationSolutionId: Yup.number()
    .positive("Integration Solution is required")
    .typeError("Integration Solution is required")
    .required("Integration Solution is required"),
  name: Yup.string()
    .trim()
    .max(100, "Cannot be greater than 100 characters")
    .required("Add-in Name is required"),
  description: Yup.string()
    .trim()
    .max(500, "Cannot be greater than 500 characters")
    .required("Add-in Description is required"),
  applicableTo: Yup.array()
    .min(1, "At least one option must be selected")
    .required("Applicable To is required"),
};

// Step 2: Subscription Details validation (costType: 1 = free, 2 = paid)
const AddInsStep2Schema = {
  subscriptionDetails: Yup.object().shape({
    costType: Yup.number().oneOf([1, 2]).required(),
    subscriberPlans: Yup.array().when("costType", (costType, schema) => {
      if (costType === 2) {
        return schema
          .of(SubscriptionPlanSchema)
          .test(
            "subscriber-validation",
            "Invalid subscriber plan data",
            function (value) {
              // Access root formik values via this.from
              const rootValues = this.from?.[1]?.value;

              // Check if subscriber is applicable
              if (rootValues?.applicableTo?.includes(1)) {
                // Must have at least one plan
                if (!value || value.length === 0) {
                  this.createError({
                    message: "At least one subscriber plan is required",
                    path: this.path,
                  });
                  return false;
                }

                // Validate each plan individually
                for (let i = 0; i < value.length; i++) {
                  const plan = value[i];
                  try {
                    SubscriptionPlanSchema.validateSync(plan, {
                      abortEarly: false,
                    });
                  } catch (error) {
                    this.createError({
                      message: `Subscriber plan ${i + 1}: ${error.message}`,
                      path: `${this.path}[${i}]`,
                    });
                    return false;
                  }
                }
              }
              return true;
            },
          );
      }
      return schema.nullable();
    }),
    enterprisePlans: Yup.array().when("costType", (costType, schema) => {
      if (costType === 2) {
        return schema
          .of(EnterpriseSubscriptionPlanSchema)
          .test(
            "enterprise-validation",
            "Invalid enterprise plan data",
            function (value) {
              // Access root formik values via this.from
              const rootValues = this.from?.[1]?.value;

              // Check if enterprise is applicable
              if (rootValues?.applicableTo?.includes(2)) {
                // Must have at least one plan
                if (!value || value.length === 0) {
                  this.createError({
                    message: "At least one enterprise plan is required",
                    path: this.path,
                  });
                  return false;
                }

                // Validate each plan individually
                for (let i = 0; i < value.length; i++) {
                  const plan = value[i];
                  try {
                    EnterpriseSubscriptionPlanSchema.validateSync(plan, {
                      abortEarly: false,
                    });
                  } catch (error) {
                    this.createError({
                      message: `Enterprise plan ${i + 1}: ${error.message}`,
                      path: `${this.path}[${i}]`,
                    });
                    return false;
                  }
                }
              }
              return true;
            },
          );
      }
      return schema.nullable();
    }),
  }),
};

// Complete schema combining both steps
const AddInsSchema = {
  ...AddInsStep1Schema,
  ...AddInsStep2Schema,
};

// Exported schemas for step-wise validation
export const AddInsStep1ValidationSchema = Yup.object().shape({
  ...AddInsStep1Schema,
});

export const AddInsStep2ValidationSchema = Yup.object().shape({
  ...AddInsStep2Schema,
});

export const AddInSchema = Yup.object().shape({
  ...AddInsSchema,
});
